import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface SelectOption {
  id: string;
  name: string;
  path?: string;
  description?: string;
}

/**
 * Custom hook for managing persistent selection state across localStorage and URL parameters
 *
 * @param storageKey - The key to use for localStorage
 * @param urlParam - The URL search parameter name
 * @param items - Array of items to select from
 * @returns Tuple of [selectedValue, setSelectedValue] similar to useState
 *
 * @example
 * ```tsx
 * const [selectedProject, setSelectedProject] = usePersistentSelection(
 *   'lastSelectedConfigProject',
 *   'solution',
 *   projectsList
 * );
 * ```
 */
export function usePersistentSelection<T extends SelectOption>(
  storageKey: string,
  urlParam: string,
  items: T[],
): [T | null, (value: T | null) => void] {
  const [value, setValue] = useState<T | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Load initial value from URL or localStorage
  useEffect(() => {
    if (items.length === 0) return;

    // Priority 1: URL parameter
    const urlParamId = searchParams.get(urlParam);
    if (urlParamId) {
      const itemFromUrl = items.find((item) => item.id === urlParamId);
      if (itemFromUrl) {
        setValue(itemFromUrl);
        return;
      }
    }

    // Priority 2: localStorage
    const savedId = localStorage.getItem(storageKey);
    if (savedId) {
      const savedItem = items.find((item) => item.id === savedId);
      if (savedItem) {
        setValue(savedItem);
      }
    }
  }, [items, searchParams, storageKey, urlParam]);

  // Persist value to URL and localStorage when it changes
  useEffect(() => {
    // Don't modify URL during OAuth callback or if critical OAuth parameters exist
    const hasOAuthParams = searchParams.has('code') || searchParams.has('state');
    const isOAuthCallback = window.location.pathname.includes('oauth-callback');

    if (hasOAuthParams || isOAuthCallback) {
      return; // Skip URL modification during OAuth flow
    }

    const currentUrlParam = searchParams.get(urlParam);

    if (value) {
      // Save to localStorage
      localStorage.setItem(storageKey, value.id);

      // Only update URL if the current parameter doesn't match the selected value
      if (currentUrlParam !== value.id) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set(urlParam, value.id);
        setSearchParams(newSearchParams);
      }
    } else if (!value && currentUrlParam) {
      // If no value is selected but URL has a parameter, remove it
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(urlParam);
      setSearchParams(newSearchParams);
    }
  }, [value, searchParams, setSearchParams, storageKey, urlParam]);

  return [value, setValue];
}
